/**
 * Atomically edit elements.
 *
 * The functions call the approriate setters on the MST models.
 */

import { v4 as uuid } from 'uuid';
import type { IObjectType } from '../../../../stores/model/metamodel/object_type';
import type { IEventType } from '../../../../stores/model/metamodel/event_type';
import type { IMethod } from '../../../../stores/model/metamodel/method';
import type { IDependency } from '../../../../stores/model/metamodel/dependency';
import type { IInheritance } from '../../../../stores/model/metamodel/inheritance';
import type { IDataType } from '../../../../stores/model/metamodel/data_type';
import type { IFSM } from '../../../../stores/model/metamodel/fsm';
import type { IState } from '../../../../stores/model/metamodel/state';
import type { ITransition } from '../../../../stores/model/metamodel/transition';
import type { IAttribute } from '../../../../stores/model/metamodel/attribute';
import type { IMultiplePropagationConstraint } from '../../../../stores/model/metamodel/multiple_propagation_constraint';
import type { DependencyName, DependencyType, MethodType, MPCType } from '../../types';

export function AEMC_Object(
  objectType: IObjectType,
  name: string,
  description: string,
  constraints: string,
  abstract: boolean
): void {
  objectType.setName(name);
  objectType.setDescription(description);
  objectType.setAbstract(abstract);
  objectType.setConstraints(constraints);
}

export function AEMC_Dependency(
  dependency: IDependency,
  name: DependencyName,
  type: DependencyType
): void {
  // We don't allow changing master or dependent
  // -> create another dependency and delete this one instead
  dependency.setName(name);
  dependency.setType(type);
}

export function AEMC_Event(eventType: IEventType, name: string, description: string): void {
  eventType.setName(name);
  eventType.setDescription(description);
}

export function AEMC_SpecialiseEvent(
  eventType: IEventType,
  ownerEvent: IEventType,
  inheritance: IInheritance
): void {
  eventType.specialise(ownerEvent.id, inheritance.id);
}

export function AEMC_ConvertToNormalEvent(eventType: IEventType): void {
  eventType.convertToNormal();
}

export function AEMC_RenameMethod(method: IMethod, name: string): void {
  method.setName(name);
}

export function AEMC_Method(
  method: IMethod,
  name: string,
  type: MethodType,
  precondition: string,
  implementation: string,
  postcondition: string
): void {
  method.setName(name);
  method.setType(type);
  method.setPrecondition(precondition);
  method.setImplementation(implementation);
  method.setPostcondition(postcondition);
}

export function AEMC_SpecialiseMethod(method: IMethod, viaMethod: IMethod): void {
  method.specialise(viaMethod.id);
}

export function AEMC_ConvertToOwnedMethod(method: IMethod): void {
  method.makeOwned();
}

export function AEMC_Attribute(
  attribute: IAttribute,
  name: string,
  type: string,
  description: string,
  implementation: string,
  validation: string
): void {
  attribute.setName(name);
  attribute.setType(type);
  attribute.setDescription(description);
  attribute.setImplementation(implementation);
  attribute.setValidation(validation);
}

export function AEMC_DataType(dataType: IDataType, name: string): void {
  dataType.setName(name);
}

export function AEMC_MultiplePropagationConstraint(
  mpc: IMultiplePropagationConstraint,
  type: MPCType
): void {
  mpc.setType(type);
}

export function AEMC_FSM(fsm: IFSM, name: string, codegeneration: boolean): void {
  fsm.setName(name);
  fsm.setCodeGeneration(codegeneration);
}

export function AEMC_Transition(transition: ITransition, methods: IMethod[]): void {
  const transitionMethods = methods.map((m) => {
    let id = uuid();
    const existing = transition.findMethod(m);
    if (existing) {
      id = existing.id;
    }
    return {
      id,
      method: m.id,
      methodName: m.name,
    };
  });
  // setMethods is typed against the TransitionMethod *instance*, but MST accepts
  // the plain creation snapshot ({ id, method, methodName }) at runtime.
  transition.setMethods(transitionMethods as unknown as Parameters<ITransition['setMethods']>[0]);
}

export function AEMC_State(state: IState, name: string): void {
  state.setName(name);
}
